// StUpEco Workspace PWA helpers
// Handles service worker registration, install prompt capture, install detection, and Web Push subscribe.
import { api } from "@/lib/api";

let deferredInstallPrompt = null;
const listeners = new Set();

export function isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)").matches ||
    window.navigator.standalone === true // iOS Safari
  );
}

export function isIos() {
  const ua = navigator.userAgent || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

export function isAndroid() {
  return /Android/i.test(navigator.userAgent || "");
}

export function canInstall() {
  return !!deferredInstallPrompt;
}

export function subscribeInstallable(cb) {
  listeners.add(cb);
  cb({ canInstall: canInstall(), installed: isStandalone() });
  return () => listeners.delete(cb);
}

function notify() {
  const state = { canInstall: canInstall(), installed: isStandalone() };
  listeners.forEach((cb) => cb(state));
}

export async function triggerInstallPrompt() {
  if (!deferredInstallPrompt) return { outcome: "unavailable" };
  const evt = deferredInstallPrompt;
  deferredInstallPrompt = null;
  notify();
  evt.prompt();
  const choice = await evt.userChoice.catch(() => ({ outcome: "dismissed" }));
  return choice;
}

export function registerPwa() {
  if (typeof window === "undefined") return;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    deferredInstallPrompt = e;
    notify();
  });
  window.addEventListener("appinstalled", () => {
    deferredInstallPrompt = null;
    notify();
  });

  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      navigator.serviceWorker
        .register("/sw.js")
        .catch((err) => {
          // eslint-disable-next-line no-console
          console.warn("SW registration failed:", err);
        });
    });
  }
}

// -------------------- Web Push --------------------
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const b64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

export function isPushSupported() {
  return typeof window !== "undefined" && "serviceWorker" in navigator && "PushManager" in window && "Notification" in window;
}

export async function currentPushSubscription() {
  if (!isPushSupported()) return null;
  const reg = await navigator.serviceWorker.getRegistration();
  if (!reg) return null;
  return reg.pushManager.getSubscription();
}

export async function enablePush() {
  if (!isPushSupported()) throw new Error("This browser doesn't support push notifications.");
  if (isIos() && !isStandalone()) throw new Error("On iPhone/iPad you must first Add to Home Screen and open Workspace from the Home Screen icon.");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("Notification permission was denied.");
  const reg = await navigator.serviceWorker.ready;
  const { data } = await api.get("/push/vapid-public");
  const applicationServerKey = urlBase64ToUint8Array(data.public_key);
  let sub = await reg.pushManager.getSubscription();
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey });
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";
  await api.post("/push/subscribe", {
    subscription: sub.toJSON(),
    device_label: `${navigator.platform || "Device"} · ${navigator.userAgent.slice(0, 80)}`,
    timezone,
  });
  return sub;
}

export async function disablePush() {
  const sub = await currentPushSubscription();
  if (sub) {
    try { await api.delete("/push/subscribe", { data: { endpoint: sub.endpoint } }); } catch { /* noop */ }
    try { await sub.unsubscribe(); } catch { /* noop */ }
  }
}
